import { Button } from '@/components/ui/button';

/**
 * Server-side pagination controls, shared by every list page.
 *
 * `total` must be the server-side count for the ACTIVE filter set (not the
 * length of the current page), otherwise the range and page count go wrong as
 * soon as a filter is applied. Pages that cannot supply a real total should not
 * render this.
 *
 * Renders nothing when everything already fits on one page.
 */
export const DEFAULT_PAGE_SIZE = 25;

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Noun for the range label, e.g. "licenses". */
  label?: string;
}

export function Pagination({ page, pageSize, total, onPageChange, label }: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col gap-2 border-t px-2 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <span className="text-muted-foreground">
        Showing {first}–{last} of {total}
        {label ? ` ${label}` : ''}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
        >
          Previous
        </Button>
        <span className="text-muted-foreground whitespace-nowrap">
          Page {page} of {pageCount}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
